import React, {useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import BiggerPicture from "libs/BiggerPicture"; // path to file
import {useImages} from "../../contexts/images";
import {useSetting} from "../../contexts/setting";
import {useBgTransformContext} from "../../contexts/bgTransform";
import {GRID_CONFIG} from "../../data/constants";
import "./index.css";
import cn from "classnames";
import {IMG, ZOOMIN, ZOOMOUT, FLIP_H, FLIP_V} from "../../data/constants";
import {GRID_COLORS} from "data/products/index";
import {useBgUrl} from "hooks/useBgUrl";
import {resolveDisplayedBgTransform} from "libs/bgTransform";
import {resolveShowGrid} from "./showGrid";
import {hasOwnTemplateImage, isReadyCut, resolveTemplateBackground} from "./templateBg";
import BakedBackground, {BakedImage} from "./BakedBackground";
import {bakedBgForTemplate, showsBakedCrop} from "libs/bakedBg";
import {registerBgOps} from "libs/bgOps";
import {getTemplateMode, isScopesChannel, withTemplateMode} from "libs/sceneScope";
import {useFeature} from "hooks/useFeature";
import {getSizeFromBase64} from "libs/bg-utils";
import {coverFitTransform} from "libs/utils";
import {Slider} from "biggerui";
import Loader from "layout/Loader";
import GenerativeBg from "layout/GenerativeBg";

/**
 * Inner component — only mounts when src is available.
 * Subscribes to useImages/useSetting contexts for transform controls.
 *
 * `baked` is the banner's baked cut of `src` (libs/bakedBg.ts), or null. The
 * banner shows the cut while it shows the crop the cut was made at and its
 * controls are closed; else it shows `src` positioned.
 */
const BgTransformerInner = ({
  src,
  id,
  blurOverride,
  fallbackTransformId,
  defaultTransform,
  extraTools,
  isLocked,
  opacityPercent,
  brightnessPercent,
  showGrid,
  gridStyle,
  baked,
  sharedImage,
}) => {
  const apiRef = useRef(null);
  const wrapperRef = useRef(null);
  const {
    isBgToolsExpanded,
    editIsBgToolsExpanded,
    editIsImageToolsExpanded,
    bgOption,
    dataChannel,
    isControlledBanner,
    templateModes,
    editTemplateModes,
    selectedTemplates,
    editSelectedTemplates,
    editScopeMode,
  } = useSetting();
  // Scene scopes (libs/sceneScope.ts): opening a banner's background tools
  // makes it the selection, so the sidebar's sliders and operations reach this
  // banner alone; the panel itself keeps only direct manipulation — zoom, fit,
  // flip, rotate. Off a scopes channel the panel carries its own sliders.
  const scoped = !isControlledBanner && isScopesChannel(dataChannel);
  const markLookOwn = () => {
    if (scoped && getTemplateMode(templateModes, id, "look") !== "own") {
      editTemplateModes(withTemplateMode(templateModes, id, "look", "own"));
    }
  };
  const {sceneId: savedSceneId} = useParams();

  const {
    bgBlurValue,
    bgX,
    editBgX,
    bgY,
    editBgY,
    bgScale,
    editBgScale,
    bgRotate,
    editBgRotate,
    bgFlip,
    editBgFlip,
    editBgOpacities,
    editBgBrightnesses,
    setUserBgAdjustment,
    clearUserBgAdjustment,
    customBg,
    customBgs,
    customBgUploaded,
  } = useImages();

  // Phase-2 derivation (issue #528, 2a). When enabled, the displayed crop is
  // read from the effective-transform selector (precedence over React Query +
  // user intent) and edits are written as user intent keyed by (bgOption, id),
  // never into the context bg records. When disabled, everything below falls
  // back to the legacy context-driven read/write, byte-for-byte.
  const {enabled: bgDerivation, getEffectiveTransform} = useBgTransformContext();

  const isExpanded = isBgToolsExpanded[id] || false;
  const buttonPack = cn("button-pack", {visible: isExpanded});
  const expandBtn = cn("round-btn expander", {opaque: isExpanded});
  const wrapperClass = cn("image-wrapper", {active: isExpanded});
  const imageClass = "bg-image";

  // bgOption is this template's DISPLAYED option — the nested ALL-products
  // provider overrides it per child banner — and it's also the key
  // commitBgTransform writes adjustments under, so passing it keeps the read
  // and the write on the same variation by construction (#567, R4 follow-up).
  const eff = bgDerivation ? getEffectiveTransform(id, bgOption) : null;
  const fallbackEff =
    bgDerivation && fallbackTransformId
      ? getEffectiveTransform(fallbackTransformId, bgOption)
      : null;
  // A ready cut (templateBg.ts) — an exact-canvas-size image a default scene
  // gave this banner, like the Figma backgrounds — shows at natural size with
  // no crop. Positioning it would only misalign an already-correct picture,
  // so it has no pencil/zoom/rotate/flip tools and no scope operations. Any
  // other image keeps them: a fresh or saved upload also can start at
  // identity, but it needs them to find its crop.
  //
  // The role feature "bg.figma-cut-tools" (data/features.ts, superadmin by
  // default) gives a ready cut the pencil tools too. The scope operations
  // stay off: they reach every banner in scope, and an untouched cut must not
  // move with the others. A moved cut is no longer a ready cut.
  const readyCut =
    bgDerivation &&
    isReadyCut({
      src,
      image: customBgs?.[id],
      uploaded: customBgUploaded?.[id],
      crop: eff ?? fallbackEff,
    });
  const figmaCutTools = useFeature("bg.figma-cut-tools");
  // Role feature "bg.banner-tools": the pencil on every banner. Without it each
  // banner keeps the position that the default or the scene gives it.
  const bannerTools = useFeature("bg.banner-tools");
  const hidePositioning = !bannerTools || (readyCut && !figmaCutTools);
  const {x, y, scale, rotate, flip} = resolveDisplayedBgTransform({
    eff,
    fallbackEff,
    defaultTransform,
    templateId: id,
    fallbackTemplateId: fallbackTransformId,
    bgX,
    bgY,
    bgScale,
    bgRotate,
    bgFlip,
  });
  // Resolved by the gate below (templateBg.ts): the template's own value, a
  // preset from the default scene, or the shared slider, in that order. The
  // image alone is faded, never the wrapper: the base colour under it is what
  // shows through, which is the effect the Air designs are built on.
  const brightness = brightnessPercent;
  const blur = blurOverride ?? bgBlurValue ?? 0;
  const opacity = opacityPercent / 100;
  const isSwap = rotate === 90 || rotate === 270;

  // The baked cut stands for the positioned image while the crop is the
  // cut's. The open controls move the image itself, so they show it.
  const bakedCurrent = Boolean(baked) && showsBakedCrop(baked, {x, y, scale, rotate, flip});
  const showBaked = bakedCurrent && !isExpanded;
  // When the banner goes from its cut to the positioned image, the cut stays
  // under that image until it loads. A banner that never showed its cut (a
  // saved scene with a changed crop, as the renderer opens it) has none.
  const [cutShown, setCutShown] = useState(false);
  const [loadedSrc, setLoadedSrc] = useState(null);
  useEffect(() => {
    if (showBaked) setCutShown(true);
  }, [showBaked]);
  const showUnderlay = Boolean(baked) && cutShown && !showBaked && loadedSrc !== src;

  // Commit a background edit: as user intent (bgDerivation) or straight into
  // the context records (legacy). The intent write carries the full transform
  // so a partial edit (only rotate, only flip) preserves the other axes.
  const commitBgTransform = (next) => {
    if (bgDerivation) {
      setUserBgAdjustment(bgOption, id, {x, y, scale, rotate, flip, ...next});
      return;
    }
    if (next.x !== undefined) editBgX(id, next.x);
    if (next.y !== undefined) editBgY(id, next.y);
    if (next.scale !== undefined) editBgScale(id, next.scale);
    if (next.rotate !== undefined) editBgRotate(id, next.rotate);
    if (next.flip !== undefined) editBgFlip(id, next.flip);
  };

  const selectThisBanner = (open) => {
    if (!scoped) return;
    if (open) {
      editSelectedTemplates([id]);
      editScopeMode("selection");
    } else if (selectedTemplates.length === 1 && selectedTemplates[0] === id) {
      editSelectedTemplates([]);
      editScopeMode("all");
    }
  };

  const expand = () => {
    editIsBgToolsExpanded(id, !isExpanded);
    editIsImageToolsExpanded(false);
    selectThisBanner(!isExpanded);
  };

  const coverFit = async () => {
    const box = wrapperRef.current;
    if (!box) return;
    let imgW;
    let imgH;
    if (showBaked) {
      // The cut is on screen, not the image the crop applies to.
      try {
        ({width: imgW, height: imgH} = await getSizeFromBase64(src));
      } catch (error) {
        console.error("Error reading the background size:", error);
        return;
      }
    } else {
      const media = box.querySelector(".image-wrapper img, .image-wrapper video");
      if (!media) return;
      imgW = media.naturalWidth ?? media.videoWidth ?? 0;
      imgH = media.naturalHeight ?? media.videoHeight ?? 0;
    }
    const {scale: nextScale, x: nextX, y: nextY} = coverFitTransform(
      imgW,
      imgH,
      box.offsetWidth,
      box.offsetHeight
    );
    commitBgTransform({x: nextX, y: nextY, scale: nextScale});
  };

  // Back to the crop this banner would show with no manual edit: the user
  // intent for (bgOption, id) is dropped and the derived chain — the resolved
  // default scene, then the catalog — shows through again, rotation and flip
  // included. Off derivation the context records ARE the state, so the
  // template's declared default is written back, or a cover fit when it has
  // none.
  const resetToDefault = () => {
    if (bgDerivation) {
      clearUserBgAdjustment(bgOption, id);
      return;
    }
    if (defaultTransform) {
      commitBgTransform({
        x: defaultTransform.x,
        y: defaultTransform.y,
        scale: defaultTransform.scale,
        rotate: defaultTransform.rotate ?? 0,
        flip: defaultTransform.flip ?? {x: false, y: false},
      });
      return;
    }
    coverFit();
  };

  const handleTransform = (nextX, nextY, nextScale) => {
    commitBgTransform({x: nextX, y: nextY, scale: nextScale});
  };

  const handleFlip = (nextFlip) => {
    commitBgTransform({flip: nextFlip});
  };

  const handleRotate = (nextRotate) => {
    commitBgTransform({rotate: nextRotate});
  };

  useEffect(() => {
    if (bgRotate === null) editBgRotate(id, 0);
    if (bgFlip === null) editBgFlip(id, {x: false, y: false});
  }, [bgRotate, bgFlip]);

  // Scene scopes: the sidebar applies fit / flip / rotate to every banner in
  // scope through this registry, running the same handlers as the buttons.
  // A ready cut registers nothing, also when its pencil tools show: a default
  // save does not cut it again, and no scope operation moves it.
  useEffect(
    () =>
      readyCut
        ? undefined
        : registerBgOps(id, {
            coverFit,
            flipH: () =>
              isSwap ? handleFlip({...flip, y: !flip.y}) : handleFlip({...flip, x: !flip.x}),
            flipV: () =>
              isSwap ? handleFlip({...flip, x: !flip.x}) : handleFlip({...flip, y: !flip.y}),
            rotate: (deg) => handleRotate(((rotate ?? 0) + deg) % 360),
            reset: resetToDefault,
            // What a default save cuts this banner from (libs/bakedBg.ts): the
            // image shown, the background box, and the crop shown. A banner
            // that still shows its cut's crop keeps the cut.
            bakeSource: () => {
              const box = wrapperRef.current;
              if (!box || !src || bakedCurrent) return null;
              return {
                src,
                width: box.offsetWidth,
                height: box.offsetHeight,
                transform: {x, y, scale, rotate, flip},
              };
            },
            // What the Defaults panel copies to the other products of the
            // channel (DefaultScenePanel/replicateBg.ts): the crop shown, and
            // the background box. Also for a banner that shows its cut.
            // `sharedImage` is false for an image of the banner's own.
            positionSource: () => {
              const box = wrapperRef.current;
              if (!box || !src) return null;
              return {
                src,
                width: box.offsetWidth,
                height: box.offsetHeight,
                transform: {x, y, scale, rotate, flip},
                sharedImage,
              };
            },
          }),
    // The handlers close over the current transform; re-register on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      id,
      src,
      x,
      y,
      scale,
      rotate,
      flip,
      isSwap,
      bgDerivation,
      bgOption,
      readyCut,
      bakedCurrent,
      showBaked,
      sharedImage,
    ]
  );

  // Close the button when focus leaves the banner
  useEffect(() => {
    if (!isExpanded) return;
    const handleClickOutside = (event) => {
      // The sidebar is where a scoped banner's sliders live now, so a click
      // there keeps the panel — and the selection — open.
      if (
        !event.target.closest(`.bg-transformer[data-id="${id}"]`) &&
        !(scoped && event.target.closest("aside"))
      ) {
        editIsBgToolsExpanded(id, false);
        selectThisBanner(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [id, isExpanded, editIsBgToolsExpanded]);

  useEffect(() => {
    if (src) {
      const img = document.querySelector(".bg-transformer img");
      if (img && !img.crossOrigin) {
        img.crossOrigin = "anonymous";
      }
    }
  }, [src]);

  // Cover-fit a freshly-selected custom bg once its real dimensions are known.
  // Runs per rendered template — so it uses the correct id and the real box
  // (offsetWidth/Height), stays channel-agnostic, and (being async) re-applies
  // after a default-scene load has synchronously clobbered the transform.
  // Centered via coverFitTransform (background-size:cover + position:center);
  // the prior version set only `scale` and left x/y at 0, pinning uploads to
  // the top-left corner.
  //
  // Gated on: this template is actually rendering the shared custom-bg upload
  // (`src === customBg`), plus not a real saved-scene route. A custom upload
  // always replaces the background, so it must cover-fit even on channels that
  // loaded a default scene — hence NOT bailing on `hash` (set by ANY
  // default-scene load, even one using a numbered bg) or `sceneIdFromUrl` (only
  // present for numbered tileset bgs, which the `src === customBg` check
  // already excludes). That was why uploads never cover-fit on the
  // default-scene channels (email/releases/internal/landingpage).
  //
  // The `src === customBg` check (not just `bgOption === "custom"`) matters for
  // channels that render their bg from `customBgs[id]`/assets regardless of
  // bgOption — Livestream and Ads. When `bgOption === "custom"` leaks in from
  // another channel during an SPA transition, their baked defaults would
  // otherwise get cover-fit here and shoved off-frame. Those channels cover-fit
  // their own uploads explicitly (BgSelector/CustomBgUpload), so a baked
  // default here must stay at identity.
  useEffect(() => {
    if (isControlledBanner || savedSceneId || bgOption !== "custom" || !src || src !== customBg) return;

    let cancelled = false;
    (async () => {
      try {
        const templateWidth = wrapperRef.current?.offsetWidth;
        const templateHeight = wrapperRef.current?.offsetHeight;
        const {width, height} = await getSizeFromBase64(src);
        if (cancelled || !width || !height || !templateWidth || !templateHeight) return;
        const {
          scale: nextScale,
          x: nextX,
          y: nextY,
        } = coverFitTransform(width, height, templateWidth, templateHeight);
        commitBgTransform({x: nextX, y: nextY, scale: nextScale});
      } catch (error) {
        console.error("Error computing cover-fit:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the source changes
  }, [src]);

  const bakedImage = (underlay) => (
    <BakedImage
      src={baked.url}
      cropScale={baked.scale}
      brightnessPercent={brightness}
      opacityPercent={opacityPercent}
      blur={blur}
      underlay={underlay}
    />
  );

  return (
    <div
      className={cn("bg-transformer", {nogrid: !showGrid})}
      ref={wrapperRef}
      data-id={id}
      data-baked-bg={showBaked ? "" : undefined}
      style={gridStyle}
    >
      {bgOption === "generative" ? (
        <GenerativeBg />
      ) : showBaked ? (
        bakedImage(false)
      ) : (
        <>
          {showUnderlay && bakedImage(true)}
          <BiggerPicture
            ref={apiRef}
            src={src}
            wrapperClassName={wrapperClass}
            imgClassName={imageClass}
            imgStyle={{filter: `brightness(${brightness}%) blur(${blur}px)`, opacity}}
            wrapperStyle={{pointerEvents: isExpanded ? "auto" : "none"}}
            x={x}
            y={y}
            scale={scale}
            rotate={rotate}
            flip={flip}
            minScale={0.1}
            maxScale={10}
            pinchSpeed={0.0024}
            onTransform={handleTransform}
            onLoad={() => setLoadedSrc(src)}
          />
        </>
      )}

      {bgOption !== "generative" && !hidePositioning && (
        <div className="tools">
          <button title="Edit background" className={expandBtn} onClick={expand}>
            <img src={IMG} />
          </button>
          {extraTools}
          <div className={buttonPack}>
            <button className="round-btn" onClick={() => apiRef.current?.zoomIn(0.1)}>
              <img src={ZOOMIN} />
            </button>
            <button className="round-btn" onClick={() => apiRef.current?.zoomOut(0.1)}>
              <img src={ZOOMOUT} />
            </button>
            {scoped && (
              <button
                className="round-btn rotate-90"
                title="Rotate by 90°"
                onClick={() => handleRotate(((rotate ?? 0) + 90) % 360)}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 12a8 8 0 1 1-2.6-5.9" />
                  <path d="M20 3v5h-5" />
                </svg>
              </button>
            )}
            {!scoped && (
              <div className="rotate-container">
                <div className="rotate-buttons">
                  <button className="rotate-btn" onClick={() => handleRotate(90)}>
                    90º
                  </button>
                  <button className="rotate-btn" onClick={() => handleRotate(180)}>
                    180º
                  </button>
                  <button className="rotate-btn" onClick={() => handleRotate(270)}>
                    270º
                  </button>
                </div>

                <Slider
                  id="bg-rotate"
                  min="0"
                  max="360"
                  value={rotate}
                  onChange={(e) => handleRotate(Number(e.target.value))}
                  className="rotate-slider"
                />
              </div>
            )}

            <button
              className="round-btn"
              title="Flip horizontally"
              onClick={() => {
                isSwap ? handleFlip({...flip, y: !flip.y}) : handleFlip({...flip, x: !flip.x});
              }}
            >
              <img src={FLIP_H} />
            </button>
            <button
              className="round-btn"
              title="Flip vertically"
              onClick={() => {
                isSwap ? handleFlip({...flip, x: !flip.x}) : handleFlip({...flip, y: !flip.y});
              }}
            >
              <img src={FLIP_V} />
            </button>
            {isLocked && !scoped && (
              <div className="opacity-container">
                <div className="opacity-row" title="Opacity of this template's background">
                  <span className="opacity-label">Opacity {opacityPercent}%</span>
                  <Slider
                    id={`bg-opacity-${id}`}
                    min="0"
                    max="100"
                    value={opacityPercent}
                    onChange={(e) => {
                      editBgOpacities(id, Number(e.target.value));
                      markLookOwn();
                    }}
                    className="opacity-slider"
                  />
                </div>
                <div className="opacity-row" title="Brightness of this template's background">
                  <span className="opacity-label">Brightness {brightnessPercent}%</span>
                  <Slider
                    id={`bg-brightness-${id}`}
                    min="0"
                    max="200"
                    value={brightnessPercent}
                    onChange={(e) => {
                      editBgBrightnesses(id, Number(e.target.value));
                      markLookOwn();
                    }}
                    className="opacity-slider"
                  />
                </div>
              </div>
            )}
            <button
              className="round-btn reset-default"
              title="Reset to default position"
              onClick={resetToDefault}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4 12a8 8 0 1 0 2.6-5.9" />
                <path d="M4 3v5h5" />
                <path d="M12 8v4l3 2" />
              </svg>
            </button>
            <button className="round-btn cover-fit" title="Fit to cover" onClick={coverFit}>
              <svg
                width="22"
                height="22"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6V3h3M10 3h3v3M13 10v3h-3M6 13H3v-3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * BgTransformer — gate component.
 * Shows <Loader/> while bgUrl is still loading (unless bgOption is
 * "generative", which needs no src). Otherwise mounts BgTransformerInner.
 *
 * A detached (locked) template can carry its own per-template upload in
 * customBgs[id] (see layout/CustomBgUpload). It overrides the channel's
 * shared/gallery/baked background for that one template — resolved here so
 * every channel gets per-template uploads without each template wiring it in.
 * Channels that already fold customBgs[id] into `src` (Ads, Livestream)
 * resolve to the same value, so this is a no-op for them.
 */
const BgTransformer = ({
  src,
  id,
  blurOverride = undefined,
  fallbackTransformId = undefined,
  defaultTransform = undefined,
  extraTools = undefined,
}) => {
  const {bgOption, locked, dataChannel, templateModes, product} = useSetting();
  const {
    customBgs,
    customBgUploaded,
    bgOpacities,
    bgBrightnesses,
    bgOpacity,
    bgBrightness,
    bgPresets,
    bgBlurValue,
    bakedBgs,
    bakedBgOption,
  } = useImages();
  // The gallery background this route resolved, and its style markers. Only used
  // to decide the grid overlay: the src below is what this template actually
  // renders, which may instead be a baked per-template asset or an upload.
  const {url: resolvedBgUrl, markers: resolvedBgMarkers, isLoading} = useBgUrl();
  const {awaitingDefaultScene} = useBgTransformContext();
  const figmaCutTools = useFeature("bg.figma-cut-tools");

  const isLocked = locked.some((x) => x === id);
  // Own value → preset from the default scene → shared. See templateBg.ts.
  const resolved = resolveTemplateBackground({
    id,
    isLocked,
    bgOption,
    sharedSrc: src,
    customBgs,
    customBgUploaded,
    bgOpacities,
    bgBrightnesses,
    bgOpacity,
    bgBrightness,
    bgPresets,
    imageMode: isScopesChannel(dataChannel)
      ? getTemplateMode(templateModes, id, "image")
      : undefined,
  });
  const effectiveSrc = resolved.src;

  // Grid overlay: the product always grids, or this background image is a grid
  // image. See showGrid.ts for why that needs both the resolved background's
  // markers and the filename of anything else src can be.
  //
  // The decision lives HERE, on the gate, because the placeholder below is a
  // `.bg-transformer` too: the stylesheet grids that class by default, so a
  // placeholder without the verdict painted a grey grid over the base colour
  // on every template whose background did not resolve — and the render route
  // baked it into the export.
  const showGrid = resolveShowGrid({
    productValue: product.value,
    channel: dataChannel,
    src: effectiveSrc,
    bgUrl: resolvedBgUrl,
    bgMarkers: resolvedBgMarkers,
  });
  const gridStyle = {
    "--grid-size": `${GRID_CONFIG[id] ? GRID_CONFIG[id] : 0}px`,
    "--grid-position": "-1px -1px",
    ...(GRID_COLORS[product.value] && {"--grid-color": GRID_COLORS[product.value]}),
  };

  // A baked background (libs/bakedBg.ts) stands for the positioned image,
  // unless the banner shows an image of its own.
  const baked =
    bgOption === "generative"
      ? null
      : bakedBgForTemplate({
          templateId: id,
          bgOption,
          state: {bakedBgs, option: bakedBgOption},
          hasOwnImage: hasOwnTemplateImage({id, customBgs, customBgUploaded, bgPresets}),
          shownSrc: effectiveSrc,
        });
  // The route's default decides between a baked and a positioned image: until
  // it is applied the banner waits, as for an image that is still loading.
  if (awaitingDefaultScene && bgOption !== "generative") {
    return (
      <div
        className={cn("bg-transformer", {nogrid: !showGrid})}
        data-id={id}
        data-background-state="loading"
        style={gridStyle}
      >
        <Loader />
      </div>
    );
  }
  // A baked ready cut (templateBg.ts) has no positioning controls. Any other
  // cut keeps them (BgTransformerInner), once the image it was cut from is
  // known: the controls move that image. With the role feature
  // "bg.figma-cut-tools" a ready cut keeps its pencil tools too.
  if (
    baked &&
    (!effectiveSrc ||
      (!figmaCutTools &&
        isReadyCut({
          src: baked.src ?? effectiveSrc,
          image: customBgs?.[id],
          uploaded: customBgUploaded?.[id],
          crop: baked.transform,
        })))
  ) {
    return (
      <BakedBackground
        id={id}
        src={baked.url}
        cropScale={baked.scale}
        brightnessPercent={resolved.brightnessPercent}
        opacityPercent={resolved.opacityPercent}
        blur={blurOverride ?? bgBlurValue ?? 0}
        showGrid={showGrid}
        gridStyle={gridStyle}
      />
    );
  }

  if (!effectiveSrc && bgOption !== "generative") {
    return (
      <div
        className={cn("bg-transformer", {nogrid: !showGrid})}
        data-id={id}
        data-background-state={isLoading ? "loading" : "missing"}
        style={gridStyle}
      >
        {isLoading && <Loader />}
      </div>
    );
  }
  return (
    <BgTransformerInner
      src={effectiveSrc}
      id={id}
      blurOverride={blurOverride}
      fallbackTransformId={fallbackTransformId}
      defaultTransform={defaultTransform}
      extraTools={extraTools}
      isLocked={isLocked}
      opacityPercent={resolved.opacityPercent}
      brightnessPercent={resolved.brightnessPercent}
      showGrid={showGrid}
      gridStyle={gridStyle}
      baked={baked}
      sharedImage={effectiveSrc === src}
    />
  );
};

export default BgTransformer;
